import { useSyncExternalStore } from "react";
import { BreakpointUtils, type Breakpoint } from "@/shared/utils/BreakpointUtils";

/** Reads the live viewport width. Returns a string, so repeat calls are `Object.is`-stable while the
 * breakpoint is unchanged — which is what `useSyncExternalStore` requires of a snapshot. */
function readBreakpoint(): Breakpoint {
    const width = window.innerWidth;
    let current: Breakpoint = "base";

    for (const [key, value] of Object.entries(BreakpointUtils.breakpoints)) {
        const breakpointValue = parseInt(String(value).replace("px", ""), 10);
        if (width >= breakpointValue) {
            current = key as Breakpoint;
        } else {
            break;
        }
    }

    return current;
}

/** Same 100ms debounce the previous effect-based implementation used, so momentum resize can't
 * re-render on every frame. */
function subscribe(onStoreChange: () => void) {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const debounced = () => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(onStoreChange, 100);
    };

    window.addEventListener("resize", debounced);

    return () => {
        if (timeout) clearTimeout(timeout);
        window.removeEventListener("resize", debounced);
    };
}

/**
 * The viewport is an external store, not React state: reading it through `useSyncExternalStore` keeps
 * the SSR/hydration render on the "base" server snapshot (exactly what the old `useState("base")`
 * rendered) and then swaps to the measured breakpoint, without a setState in an effect.
 */
export function useBreakpoint(): Breakpoint {
    return useSyncExternalStore(subscribe, readBreakpoint, () => "base");
}
